import { FiEdit } from 'react-icons/fi';
import { formatDate } from '../../../../utils/DateUtils';
import { formatCurrency } from '../../../../utils/CurrencyUtils';
import ModalInfo from '../../../../components/lib/modalInfo';
import { RiErrorWarningLine } from 'react-icons/ri';
import { useAuth } from '../../../../auth/AuthenticationWrapper';
import LateralPopover from '../../../../components/lib/lateralPopover';
import DetailsModal from '../../../../components/registryDetailsModal';
import { useMemo } from 'react';

const StatCard = ({ label, value, icon: Icon, colorClass, onClickEdit, canEdit }) => (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-center">
        <div>
            <p className="text-xs text-gray-500 font-bold uppercase">{label}</p>
            <p className={`text-lg font-bold ${colorClass}`}>{formatCurrency(value)}</p>
        </div>
        {canEdit && (
            <button onClick={onClickEdit} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-gray-400 hover:text-custom_blue">
                <Icon size={18} />
            </button>
        )}
    </div>
);

const TransactionRow = ({ label, count, total, details, type, index }) => (
    <div className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-lg hover:border-slate-300 transition-colors mb-2 shadow-sm">
        <div className="flex flex-col">
            <span className="text-[10px] text-gray-400 font-bold uppercase mb-0.5 tracking-wider">{type}</span>
            <span className="font-semibold text-gray-700 text-sm">{label}</span>
            {details && details.length > 0 && (
                <div className="mt-1">
                    {type === "Venda" ? (
                        <DetailsModal details={details} />
                    ) : (
                        <LateralPopover details={details} buttonIndex={`${type}-${index}`} />
                    )}
                </div>
            )}
        </div>
        <div className="text-right">
            <span className="block font-bold text-gray-800">{formatCurrency(total)}</span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">{count}x</span>
        </div>
    </div>
);

const OpenedCashView = ({
    dailyCashFlow,
    inputValue,
    outputValue,
    pedidoEntries = [],
    recargaEntries = [],
    correcaoEntries = [],
    pedidoOnlineEntries = [],
    recargaOnlineEntries = [],
    pedidoCreditoFoodzEntries = [],
    loading,
    handleEditClick,
    setIsClosed
}) => {
    const { hasPermission } = useAuth();
    const canTakeMoneyOut = hasPermission("financial", "registry_outgoing");
    const canClose = hasPermission("financial", "close_registry");

    const displayDate = useMemo(() => {
        if (!dailyCashFlow?.opening_time) return "--/--/---- --:--";
        try {
            const formatted = formatDate(dailyCashFlow.opening_time);
            return formatted.includes("NaN") ? "--/--/---- --:--" : formatted;
        } catch (e) {
            return "--/--/---- --:--";
        }
    }, [dailyCashFlow.opening_time]);

    const displayStartValue = useMemo(() => {
        const val = parseFloat(dailyCashFlow?.start_value);
        if (isNaN(val)) return "R$ 0,00";
        return formatCurrency(val);
    }, [dailyCashFlow.start_value]);

    return (
        <div className='flex flex-col h-full space-y-6'>
            <div className="grid grid-cols-2 gap-3 shrink-0">
                <div className="col-span-2 bg-blue-50 border border-blue-100 p-4 rounded-xl flex justify-between items-center shadow-sm">
                    <div>
                        <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Abertura</p>
                        <p className="text-xs text-blue-400 font-mono mt-0.5">{displayDate}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Saldo Inicial</p>
                        <p className="text-2xl font-bold text-blue-700">{displayStartValue}</p>
                    </div>
                </div>
                
                <StatCard 
                    label="Entradas (+)" 
                    value={inputValue} 
                    icon={FiEdit} 
                    colorClass="text-green-600" 
                    onClickEdit={() => handleEditClick("inputValue")} 
                    canEdit={true} 
                />
                <StatCard 
                    label="Saídas (-)" 
                    value={outputValue} 
                    icon={FiEdit} 
                    colorClass="text-red-600" 
                    onClickEdit={() => handleEditClick("outputValue")} 
                    canEdit={canTakeMoneyOut} 
                />
            </div>

            <div className='flex-1 overflow-y-auto space-y-6 pr-1 custom-scrollbar'>
                
                {pedidoEntries.length > 0 && (
                    <section>
                        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                            <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide">Vendas</h4>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 rounded-full">{pedidoEntries.length}</span>
                        </div>
                        {pedidoEntries.map((item, idx) => (
                            <TransactionRow key={`venda-${idx}`} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} type="Venda" />
                        ))}
                    </section>
                )}

                {recargaEntries.length > 0 && (
                    <section>
                        <div className='flex items-center justify-between mb-2 border-b border-slate-200 pb-1'>
                            <div className="flex items-center gap-2">
                                <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide">Recargas</h4>
                                <span className="text-xs bg-gray-100 text-gray-500 px-2 rounded-full">{recargaEntries.length}</span>
                            </div>
                            <ModalInfo icon={<RiErrorWarningLine />} content="Total bruto recebido em recargas." />
                        </div>
                        {recargaEntries.map((item, idx) => (
                            <TransactionRow key={`recarga-${idx}`} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} type="Recarga" index={idx} />
                        ))}
                    </section>
                )}

                {correcaoEntries.length > 0 && (
                    <section>
                        <div className='flex items-center justify-between mb-2 border-b border-slate-200 pb-1'>
                            <div className="flex items-center gap-2">
                                <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide">Correções</h4>
                                <span className="text-xs bg-gray-100 text-gray-500 px-2 rounded-full">{correcaoEntries.length}</span>
                            </div>
                            <ModalInfo icon={<RiErrorWarningLine />} content="Valores descontados (estornos/correções)." />
                        </div>
                        {correcaoEntries.map((item, idx) => (
                            <TransactionRow key={`correcao-${idx}`} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} type="Correção" index={idx} />
                        ))}
                    </section>
                )}

                {(pedidoOnlineEntries.length > 0 || recargaOnlineEntries.length > 0) && (
                    <section>
                        <div className='flex items-center justify-between mb-2 border-b border-slate-200 pb-1'>
                            <div className="flex items-center gap-2">
                                <h4 className="font-bold text-purple-700 text-sm uppercase tracking-wide">Pagamentos Online</h4>
                                <span className="text-xs bg-purple-50 text-purple-500 px-2 rounded-full">{pedidoOnlineEntries.length + recargaOnlineEntries.length}</span>
                            </div>
                            <ModalInfo icon={<RiErrorWarningLine />} content="Esse valor já está na carteira digital, não no caixa físico — informativo, não soma ao Valor Final." />
                        </div>
                        {pedidoOnlineEntries.map((item, idx) => (
                            <TransactionRow key={`venda-online-${idx}`} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} type="Venda Online" />
                        ))}
                        {recargaOnlineEntries.map((item, idx) => (
                            <TransactionRow key={`recarga-online-${idx}`} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} type="Recarga Online" index={idx} />
                        ))}
                    </section>
                )}

                {pedidoCreditoFoodzEntries.length > 0 && (
                    <section>
                        <div className='flex items-center justify-between mb-2 border-b border-slate-200 pb-1'>
                            <div className="flex items-center gap-2">
                                <h4 className="font-bold text-amber-700 text-sm uppercase tracking-wide">Crédito Foodz</h4>
                                <span className="text-xs bg-amber-50 text-amber-600 px-2 rounded-full">{pedidoCreditoFoodzEntries.length}</span>
                            </div>
                            <ModalInfo icon={<RiErrorWarningLine />} content="Esse valor já entrou no caixa quando o crédito foi recarregado — informativo, não soma ao Valor Final." />
                        </div>
                        {pedidoCreditoFoodzEntries.map((item, idx) => (
                            <TransactionRow key={`venda-foodz-${idx}`} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} type="Venda Crédito Foodz" />
                        ))}
                    </section>
                )}

                {pedidoEntries.length === 0 && recargaEntries.length === 0 && correcaoEntries.length === 0 &&
                    pedidoOnlineEntries.length === 0 && recargaOnlineEntries.length === 0 && pedidoCreditoFoodzEntries.length === 0 && (
                    <div className="text-center py-10 text-gray-400">
                        <p>Nenhuma movimentação registrada ainda.</p>
                    </div>
                )}
            </div>

            <div className="pt-4 border-t border-slate-100 shrink-0">
                <button
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                    onClick={() => setIsClosed(true)}
                    disabled={!canClose || loading}
                >
                    {loading ? "Processando..." : "Iniciar Fechamento de Caixa"}
                </button>
            </div>
        </div>
    );
};

export default OpenedCashView;